/**
 * WebSocketManager — low-level WebSocket connection with
 * automatic reconnection (exponential backoff) and lifecycle events.
 */

const BACKOFF_INITIAL_MS = 1000;
const BACKOFF_MAX_MS = 30_000;
const BACKOFF_MULTIPLIER = 2;

type ReconnectMeta = {
  attempt: number;
  delayMs: number;
  closeCode: number;
  closeReason: string;
};

export type WsLifecycleEvent = 'open' | 'close' | 'message' | 'error' | 'reconnecting';

type WsEventHandler<K extends WsLifecycleEvent> =
  K extends 'open' ? () => void :
  K extends 'close' ? (code: number, reason: string) => void :
  K extends 'message' ? (data: string) => void :
  K extends 'reconnecting' ? (meta: ReconnectMeta) => void :
  K extends 'error' ? (err: Event) => void :
  never;

export class WebSocketManager {
  private ws: WebSocket | null = null;
  private url: string;
  private backoff = BACKOFF_INITIAL_MS;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private _isConnected = false;
  private intentionalClose = false;
  private listeners = new Map<WsLifecycleEvent, Set<(...args: unknown[]) => void>>();

  constructor(url: string) {
    this.url = url;
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  get isConnected(): boolean {
    return this._isConnected;
  }

  connect(): void {
    this.intentionalClose = false;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    this.clearReconnect();
    this.doConnect();
  }

  disconnect(): void {
    this.intentionalClose = true;
    this.clearReconnect();
    this.backoff = BACKOFF_INITIAL_MS;
    this.reconnectAttempt = 0;
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this._isConnected = false;
  }

  send(data: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    } else {
      console.warn('[WebSocketManager] Cannot send — not connected');
    }
  }

  on<K extends WsLifecycleEvent>(event: K, handler: WsEventHandler<K>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    const set = this.listeners.get(event)!;
    set.add(handler as (...args: unknown[]) => void);
    return () => { set.delete(handler as (...args: unknown[]) => void); };
  }

  // ── Internal ───────────────────────────────────────

  private doConnect(): void {
    this.clearReconnect();

    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    try {
      this.ws = new WebSocket(this.url);
    } catch (err) {
      this.scheduleReconnect(0, 'socket_create_failed');
      return;
    }

    this.ws.onopen = () => {
      this._isConnected = true;
      this.backoff = BACKOFF_INITIAL_MS;
      this.reconnectAttempt = 0;
      this.fire('open');
    };

    this.ws.onclose = (ev: CloseEvent) => {
      this._isConnected = false;
      const closeCode = Number(ev.code) || 0;
      const closeReason = ev.reason || '';
      this.ws = null;
      this.fire('close', ev.code, ev.reason);
      if (!this.intentionalClose) {
        this.scheduleReconnect(closeCode, closeReason);
      }
    };

    this.ws.onmessage = (ev: MessageEvent) => {
      if (typeof ev.data === 'string') {
        this.fire('message', ev.data);
      }
    };

    this.ws.onerror = (ev: Event) => {
      console.error('[WebSocketManager] WebSocket error:', ev);
      this.fire('error', ev);
    };
  }

  private scheduleReconnect(closeCode: number, closeReason: string): void {
    if (this.intentionalClose) return;
    if (this.reconnectTimer) return;

    this.reconnectAttempt += 1;
    const jitterMultiplier = 0.8 + Math.random() * 0.4;
    const delayMs = Math.min(BACKOFF_MAX_MS, Math.round(this.backoff * jitterMultiplier));

    this.fire('reconnecting', {
      attempt: this.reconnectAttempt,
      delayMs,
      closeCode,
      closeReason,
    });

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.doConnect();
    }, delayMs);

    this.backoff = Math.min(this.backoff * BACKOFF_MULTIPLIER, BACKOFF_MAX_MS);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private handleVisibilityChange = (): void => {
    if (document.hidden) return;
    if (this.intentionalClose || this._isConnected) return;

    const state = this.ws?.readyState;
    const isClosedOrMissing = !this.ws || state === WebSocket.CLOSED;
    if (!isClosedOrMissing) return;

    if (this.reconnectTimer) {
      this.clearReconnect();
    }

    this.doConnect();
  };

  private fire<K extends WsLifecycleEvent>(event: K, ...args: unknown[]): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const handler of set) {
      try {
        handler(...args);
      } catch (err) {
        console.error(`[WebSocketManager] Error in '${event}' handler:`, err);
      }
    }
  }
}
